/**
 * Drop-in replacement for `<img src={getThumbnailUrl(...)}>` that routes the
 * fetch through the concurrency-limited thumbnail loader with 429 retry.
 *
 * For URLs that aren't thumbnails (data:, blob:, external), falls back to a
 * plain `<img>` with no queueing. On final failure, fires the caller-provided
 * `onError` so existing sibling-fallback logic (e.g. `.thumbnail-fallback`)
 * keeps working without any markup changes at the call sites.
 */

import React, { useEffect, useRef, useState } from 'react';
import { loadThumbnail, type ThumbnailStatus } from '../utils/thumbnailLoader';

export type ThumbnailImageProps = React.ImgHTMLAttributes<HTMLImageElement>;

function isThumbnailURL(url: string | undefined): boolean {
  return !!url && url.includes('/api/media/thumbnail');
}

type LoaderState = { status: 'idle' | ThumbnailStatus; blobUrl?: string };

function useThumbnail(url: string | undefined): LoaderState {
  const [state, setState] = useState<LoaderState>(() => ({
    status: url ? 'pending' : 'idle',
  }));

  useEffect(() => {
    if (!url) {
      setState({ status: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ status: 'pending' });
    loadThumbnail(url).then((result) => {
      if (cancelled) return;
      setState({ status: result.status, blobUrl: result.blobUrl });
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return state;
}

export const ThumbnailImage: React.FC<ThumbnailImageProps> = ({
  src,
  onError,
  alt = '',
  ...rest
}) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const shouldRoute = isThumbnailURL(src);
  const { blobUrl, status } = useThumbnail(shouldRoute ? src : undefined);

  // Thumbnail path: use the blob URL from the loader. Pass-through path: use
  // the caller's URL directly as a normal browser image load.
  const effectiveSrc = shouldRoute
    ? (status === 'loaded' ? blobUrl : undefined)
    : src;

  // On loader failure, synthesize an onError call on the real <img> element
  // so the existing `e.target.nextElementSibling` fallback pattern still works
  // at every call site without markup changes.
  useEffect(() => {
    if (!shouldRoute) return;
    if (status !== 'error' && status !== 'rate-limited') return;
    const img = imgRef.current;
    if (!img || !onError) return;
    onError({
      target: img,
      currentTarget: img,
      nativeEvent: new Event('error'),
    } as unknown as React.SyntheticEvent<HTMLImageElement, Event>);
  }, [shouldRoute, status, onError]);

  return <img ref={imgRef} src={effectiveSrc} alt={alt} onError={onError} {...rest} />;
};
